import { Navigate } from "react-router-dom";
import { useVLETools, VLEToolKey } from "@/hooks/useVLETools";
import { useSubscription } from "@/hooks/useSubscription";
import { DashboardSkeleton } from "@/components/ui/LoadingSkeleton";

interface ToolRouteGuardProps {
  toolKey: VLEToolKey;
  children: React.ReactNode;
  redirectTo?: string;
}

/**
 * Route guard that checks:
 * 1. If the specific tool is enabled for the current VLE
 * 2. If the subscription allows tool access (not expired/grace/suspended)
 * 
 * If either check fails, redirects appropriately.
 */
export function ToolRouteGuard({ 
  toolKey, 
  children, 
  redirectTo = "/business/tools" 
}: ToolRouteGuardProps) {
  const { isToolEnabled, isLoading: toolsLoading } = useVLETools();
  const { canAccessTools, status, isLoading: subLoading } = useSubscription();

  if (toolsLoading || subLoading) {
    return (
      <div className="flex h-screen items-center justify-center p-6 bg-background">
        <DashboardSkeleton />
      </div>
    );
  }

  // Check subscription first - if suspended/expired/grace, redirect to billing
  if (!canAccessTools) {
    return <Navigate to="/business/subscription" replace state={{ reason: 'subscription_' + status }} />;
  }

  // Then check if tool is enabled
  if (!isToolEnabled(toolKey)) {
    return <Navigate to={redirectTo} replace />;
  }

  return <>{children}</>;
}
